import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import useTranslation from '@src/hooks/useTranslation';
import {
	FacebookShareButton,
	FacebookMessengerShareButton,
	LineShareButton,
	EmailShareButton,
} from 'react-share';
import { useSelector } from 'react-redux';

import * as selectors from '@src/store/selectors';
import { ui } from '@src/services';
import useMediaQuery from '@src/hooks/useMediaQuery';
import { TOAST_TYPE_SUCCESS } from '@src/const/common';

import { FacebookIcon, MessengerIcon, LineIcon, EmailIcon, CopyIcon } from '@src/components/common/icon';
import Modal from '@src/components/common/modal';
import { getAppId } from '@src/components/common/shareButton/utils';

const MODAL_WIDTH = {
	desktop: 768,
	tablet: 688,
};

const ShareLink = styled.div`
	margin-top: 60px;
`;

const ShareLabel = styled.div`
	color: #333;
	margin-bottom: 8px;
`;

const LinkWrapper = styled.div`
	display: flex;
	width: 460px;
	height: 48px;
	background: #ECECEC;
	border-radius: 8px;
	padding: 14px 12px;
	position: relative;

	@media ${props => props.theme.device.smTablet} {
		width: 432px;
	}

	@media ${props => props.theme.device.mobile} {
		width: 343px;
	}
`;

const LinkBox = styled.div`
	color: #676767;
	font-size: 14px;
	align-items: center;
	overflow: hidden;
`;

const CopyButton = styled.div`
	width: 48px;
	height: 48px;
	background: #ECECEC;
	border-radius: 0px 8px 8px 0px;
	position: absolute;
	right: 0px;
	top: 0px;
	display: flex;
	align-items: center;
	justify-content: center;

	${(props: { isOverflow: boolean }) => (props.isOverflow ? 'box-shadow: -6px 0px 6px 0px rgba(0,0,0,0.15);' : '')}
`;

const StyledCopyIcon = styled(CopyIcon)`
	fill: #101010;
	cursor: pointer;
`;

const ModalContent = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
`;

const MediasWrapper = styled.div`
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	width: 460px;

	@media ${props => props.theme.device.smTablet} {
		width: 180px;
		grid-template-columns: repeat(2, 1fr);
		grid-row-gap: 40px;
		grid-column-gap: 40px;
	}

	@media ${props => props.theme.device.mobile} {
		width: 180px;
		grid-template-columns: repeat(2, 1fr);
		grid-row-gap: 40px;
		grid-column-gap: 40px;
	}
`;

const MediaItem = styled.div`
	display: inline-flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;

	& > *:not(:first-child) {
		margin-top: 12px;
	}
`;

const getIsOverflow = (mediaMatch, urlLength) => {
	const { isMobile, isSmTablet } = mediaMatch;
	if (isMobile) {
		return urlLength > 280;
	}
	if (isSmTablet) {
		return urlLength > 372;
	}
	return urlLength > 400;
};

interface IShareModal {
	isActive: boolean;
	handleClose: () => void;
	headerText: string;
}

const ShareModal = ({ isActive, headerText, handleClose }: IShareModal):JSX.Element => {
	const linkBoxRef = useRef(null);
	const { t } = useTranslation();
	const [urlLength, setUrlLength] = useState(0);
	const { mediaMatch } = useMediaQuery();
	const currentUrl = window.location.href;
	const isOverflow = getIsOverflow(mediaMatch, urlLength);
	const domain = useSelector(selectors.getDomain) as string;
	const appId = getAppId({ domain });

	const handleCopy = () => {
		window.navigator.clipboard.writeText(currentUrl);
		ui.toastShow({ message: t('share_button:link_has_been_copied'), type: TOAST_TYPE_SUCCESS });
	};

	useEffect(() => {
		if (urlLength === 0) {
			setUrlLength(linkBoxRef?.current.clientWidth);
		}
	}, [urlLength]);

	return (
		<Modal
			modalWidth={MODAL_WIDTH}
			isActive={isActive}
			headerText={headerText}
			handleClose={handleClose}
		>
			<ModalContent>
				<MediasWrapper>
					<FacebookShareButton url={currentUrl}>
						<MediaItem>
							<FacebookIcon />
							<span>{t('share_button:facebook')}</span>
						</MediaItem>
					</FacebookShareButton>
					<FacebookMessengerShareButton url={currentUrl} appId={appId}>
						<MediaItem>
							<MessengerIcon />
							<span>{t('share_button:messenger')}</span>
						</MediaItem>
					</FacebookMessengerShareButton>
					<LineShareButton url={currentUrl}>
						<MediaItem>
							<LineIcon />
							<span>{t('share_button:line')}</span>
						</MediaItem>
					</LineShareButton>
					<EmailShareButton url={currentUrl}>
						<MediaItem>
							<EmailIcon />
							<span>{t('share_button:email')}</span>
						</MediaItem>
					</EmailShareButton>
				</MediasWrapper>
				<ShareLink>
					<ShareLabel>{t('share_button:page_link')}</ShareLabel>
					<LinkWrapper>
						<LinkBox ref={linkBoxRef}>{currentUrl}</LinkBox>
						<CopyButton isOverflow={isOverflow}><StyledCopyIcon onClick={handleCopy} /></CopyButton>
					</LinkWrapper>
				</ShareLink>
			</ModalContent>
		</Modal>
	);
};

export default ShareModal;
